// Small, self-contained, keyboard-accessible form primitives for the public
// Start-a-Company wizard. No shared Input/Select primitive exists in the design
// system (SelfApplyPage uses raw inputs), so we build a minimal, consistent set
// here with visible focus rings and label association.

import * as React from 'react'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { wizardStepperI18n } from '../../i18n/start-company/wizard-stepper'

const fieldInputClass =
  'w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2.5 text-sm text-[var(--color-text)] shadow-sm transition-colors placeholder:text-[var(--color-text-muted)] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:cursor-not-allowed disabled:bg-[var(--color-surface-muted)]'

let _idSeq = 0
function useFieldId(explicit?: string): string {
  const [id] = React.useState(() => explicit ?? `sc-field-${++_idSeq}`)
  return id
}

function Label({
  htmlFor,
  children,
  required,
  hint,
}: {
  htmlFor: string
  children: React.ReactNode
  required?: boolean
  hint?: string
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-[var(--color-text)]">
      {children}
      {required && <span className="ml-0.5 text-[var(--color-danger)]">*</span>}
      {hint && <span className="ml-1 text-xs font-normal text-[var(--color-text-muted)]">({hint})</span>}
    </label>
  )
}

function FieldError({ children }: { children?: React.ReactNode }) {
  if (!children) return null
  return (
    <p className="mt-1 text-xs text-[var(--color-danger)]" role="alert">
      {children}
    </p>
  )
}

type TextFieldProps = {
  label: string
  value: string
  onChange: (v: string) => void
  required?: boolean
  hint?: string
  error?: string
  type?: string
  placeholder?: string
  autoComplete?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  id?: string
}

export function TextField({
  label,
  value,
  onChange,
  required,
  hint,
  error,
  type = 'text',
  placeholder,
  autoComplete,
  inputMode,
  id,
}: TextFieldProps) {
  const fid = useFieldId(id)
  return (
    <div>
      <Label htmlFor={fid} required={required} hint={hint}>
        {label}
      </Label>
      <input
        id={fid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={Boolean(error)}
        className={fieldInputClass}
      />
      <FieldError>{error}</FieldError>
    </div>
  )
}

export function TextArea({
  label,
  value,
  onChange,
  required,
  hint,
  error,
  placeholder,
  rows = 3,
  id,
}: Omit<TextFieldProps, 'type' | 'autoComplete' | 'inputMode'> & { rows?: number }) {
  const fid = useFieldId(id)
  return (
    <div>
      <Label htmlFor={fid} required={required} hint={hint}>
        {label}
      </Label>
      <textarea
        id={fid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        aria-invalid={Boolean(error)}
        className={cn(fieldInputClass, 'resize-y')}
      />
      <FieldError>{error}</FieldError>
    </div>
  )
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  required,
  hint,
  error,
  placeholder,
  id,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  required?: boolean
  hint?: string
  error?: string
  placeholder?: string
  id?: string
}) {
  const fid = useFieldId(id)
  return (
    <div>
      <Label htmlFor={fid} required={required} hint={hint}>
        {label}
      </Label>
      <select
        id={fid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        className={fieldInputClass}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <FieldError>{error}</FieldError>
    </div>
  )
}

export function RadioGroup({
  label,
  name,
  value,
  onChange,
  options,
  required,
  error,
}: {
  label: string
  name: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string; description?: string }[]
  required?: boolean
  error?: string
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 block text-sm font-medium text-[var(--color-text)]">
        {label}
        {required && <span className="ml-0.5 text-[var(--color-danger)]">*</span>}
      </legend>
      <div className="space-y-2">
        {options.map((o) => {
          const checked = value === o.value
          return (
            <label
              key={o.value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                checked
                  ? 'border-brand-400 bg-brand-50/60 ring-1 ring-brand-500/30'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-border-strong)]',
              )}
            >
              <input
                type="radio"
                name={name}
                value={o.value}
                checked={checked}
                onChange={() => onChange(o.value)}
                className="mt-0.5 h-4 w-4 accent-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-[var(--color-text)]">{o.label}</span>
                {o.description && (
                  <span className="block text-xs text-[var(--color-text-muted)]">{o.description}</span>
                )}
              </span>
            </label>
          )
        })}
      </div>
      <FieldError>{error}</FieldError>
    </fieldset>
  )
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
  error,
  id,
}: {
  label: React.ReactNode
  checked: boolean
  onChange: (v: boolean) => void
  hint?: string
  error?: string
  id?: string
}) {
  const fid = useFieldId(id)
  return (
    <div>
      <label htmlFor={fid} className="flex cursor-pointer items-start gap-3">
        <input
          id={fid}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
        />
        <span className="min-w-0 text-sm text-[var(--color-text)]">
          {label}
          {hint && <span className="block text-xs text-[var(--color-text-muted)]">{hint}</span>}
        </span>
      </label>
      <FieldError>{error}</FieldError>
    </div>
  )
}

// Horizontal wizard progress. Steps before the active one read as done (green),
// the active step is highlighted, future steps stay neutral.
export function WizardStepper({
  steps,
  current,
}: {
  steps: string[]
  current: number
}) {
  const t = useT(wizardStepperI18n)
  return (
    <ol className="flex items-center gap-1 overflow-x-auto pb-1" aria-label={t.progress}>
      {steps.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'todo'
        return (
          <li key={label} className="flex flex-1 items-center gap-1 whitespace-nowrap">
            <span
              className={cn(
                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                state === 'done' && 'bg-emerald-600 text-white',
                state === 'active' && 'bg-[var(--color-action)] text-[var(--color-action-foreground)] ring-2 ring-brand-500/30',
                state === 'todo' && 'bg-[var(--color-border)] text-[var(--color-text-muted)]',
              )}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span
              className={cn(
                'hidden text-xs font-medium sm:inline',
                state === 'active' ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]',
              )}
            >
              {label}
            </span>
            {i < steps.length - 1 && (
              <span
                className={cn(
                  'ml-1 hidden h-px flex-1 sm:block',
                  i < current ? 'bg-emerald-400' : 'bg-[var(--color-border)]',
                )}
                aria-hidden="true"
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}
